import { type HttpClientInterface, type HttpClientRequestConfig } from 'core/infra/http/HttpClient'

import { type AttachmentDocumentTypeOption } from '../domain/Document'
import { type Shipper, type ShipperDetail } from '../domain/shippers'
import {
  type RawShipperDetail,
  type RawShippersResponse,
  type RawUploadDocumentsResult,
  mapApiToShipperDetail,
  mapApiToShippers,
} from './ShipperMapper'

export type ShipperRepositoryDependencies = {
  httpClient: HttpClientInterface
}

export type GetShippersParams = {
  limit?: number
  offset?: number
  search?: string
}

export type GetShippersResult = {
  shippers: Shipper[]
  totalCount: number
}

export class ShipperRepository {
  private httpClient: HttpClientInterface

  constructor({ httpClient }: ShipperRepositoryDependencies) {
    this.httpClient = httpClient
  }

  async getShippers(
    params: GetShippersParams = {},
    config?: Partial<HttpClientRequestConfig>
  ): Promise<GetShippersResult> {
    const { limit = 10, offset = 0, search } = params
    const endpoint = `/api/v2/shipperguide/carriers/shippers`

    const response = await this.httpClient.get<RawShippersResponse>(endpoint, {
      ...config,
      params: {
        limit,
        offset,
        ...(search && { search }),
      },
    })

    try {
      return mapApiToShippers(response.data)
    } catch (error) {
      throw new Error('Failed to map API return to Shippers', { cause: error as Error })
    }
  }

  async getShipperByUUID(
    uuid: string,
    config?: Partial<HttpClientRequestConfig>
  ): Promise<ShipperDetail> {
    const endpoint = `/api/v2/shipperguide/carriers/shippers/${uuid}`

    const response = await this.httpClient.get<RawShipperDetail>(endpoint, config)

    try {
      return mapApiToShipperDetail(response.data)
    } catch (error) {
      throw new Error('Failed to map API return to ShipperDetail', { cause: error as Error })
    }
  }

  async uploadShipperDocuments(
    uuid: string,
    formData: FormData,
    config?: Partial<HttpClientRequestConfig>
  ): Promise<RawUploadDocumentsResult> {
    const endpoint = `/api/v2/shipperguide/shippers/${uuid}/attachments`

    const response = await this.httpClient.post<RawUploadDocumentsResult>(endpoint, formData, {
      ...config,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async listAttachmentDocumentTypes(
    config?: Partial<HttpClientRequestConfig>
  ): Promise<AttachmentDocumentTypeOption[]> {
    const endpoint = '/api/v2/shipperguide/generic-attachment-document-types'

    const response = await this.httpClient.get<AttachmentDocumentTypeOption[]>(endpoint, config)
    return Array.isArray(response.data) ? response.data : []
  }
}
